<template>
    <div class="container-fluid marketplace-products">
        <div class="row">
            <div class="col-lg-12">
                <div class="card mb-3">
                    <div class="card-body">
                        <div class="d-flex flex-wrap align-items-center justify-content-between">
                            <div class="d-flex flex-wrap align-items-baseline mb-2">
                                <h4 class="mr-3 mb-0 text-nowrap">판매자배송 상품관리</h4>
                                <small class="text-muted">윙 등록상품 중 판매자배송 옵션 · {{ vendorId || '판매자코드 없음' }}</small>
                            </div>
                            <div class="d-flex align-items-center mb-2 marketplace-products__tools">
                                <b-form-input v-model="keyword" size="sm" placeholder="상품명·옵션ID·바코드" class="mr-2 mb-1 marketplace-products__search" />
                                <b-button variant="primary" size="sm" class="mr-1 mb-1 text-nowrap" :disabled="busy" @click="search">
                                    <i class="fas fa-search mr-1"></i> 조회
                                </b-button>
                                <b-button variant="primary" size="sm" class="mb-1 text-nowrap" :disabled="busy || !vendorId" @click="collect">
                                    <i class="fas fa-download mr-1"></i> 상품 모으기
                                </b-button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-lg-12" v-if="busy">
                <div class="text-center text-muted py-3">{{ busyText }}</div>
            </div>
            <div class="col-lg-12" v-if="errorText">
                <b-alert show variant="danger" dismissible @dismissed="errorText = ''">{{ errorText }}</b-alert>
            </div>
            <div class="col-lg-12" v-if="noticeText">
                <b-alert show variant="info" dismissible @dismissed="noticeText = ''">{{ noticeText }}</b-alert>
            </div>

            <div class="col-lg-12" v-if="loaded">
                <div class="card">
                    <div class="card-body">
                        <div v-if="!products.length" class="text-center py-5">
                            <h5 class="mb-2">아직 모은 판매자배송 상품이 없습니다</h5>
                            <p class="text-muted mb-0">쿠팡 윙에 로그인한 크롬에서 «상품 모으기» 를 눌러 주세요.</p>
                        </div>
                        <template v-else>
                            <div class="small text-muted mb-2">
                                옵션 {{ products.length.toLocaleString() }}개 · 판매량은 판매장부(Open API 매출) 기준 {{ from30 }} ~ 오늘
                            </div>
                            <b-table :items="shown" :fields="fields" small responsive stacked="md" striped hover>
                                <template #cell(image)="d">
                                    <img v-if="d.value" :src="d.value" alt="" class="marketplace-products__img" />
                                </template>
                                <template #cell(name)="d">
                                    <div class="marketplace-products__name">{{ d.item.productName }}</div>
                                    <div class="small text-muted">{{ d.item.itemName }}</div>
                                    <div class="small text-muted">옵션ID {{ d.item.vendorItemId }}</div>
                                    <div class="small text-muted">등록상품ID {{ d.item.sellerProductId }}</div>
                                    <div class="small text-muted">바코드 {{ d.item.barcode || '없음' }}</div>
                                </template>
                                <template #cell(salePrice)="d">{{ won(d.value) }}</template>
                                <!-- b-table 은 null 칸을 '' 로 넘긴다 — 빈 값 가름은 d.item 원값으로 -->
                                <template #cell(stockQuantity)="d">
                                    <span :class="empty(d.item.stockQuantity) ? 'text-muted' : ''">{{ empty(d.item.stockQuantity) ? '안 받음' : count(d.item.stockQuantity) }}</span>
                                </template>
                                <template #cell(myStock)="d">
                                    <b-form-input
                                        :value="empty(d.item.myStock) ? '' : d.item.myStock"
                                        type="number" min="0" size="sm" class="marketplace-products__stock"
                                        placeholder="적기"
                                        @change="(v) => saveStock(d.item, v)"
                                    />
                                </template>
                                <template #cell(units7)="d">{{ salesCell(d.item.units7) }}</template>
                                <template #cell(units30)="d">{{ salesCell(d.item.units30) }}</template>
                                <template #cell(sales30)="d">{{ empty(d.item.sales30) ? '안 모음' : won(d.item.sales30) }}</template>
                            </b-table>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'

function ymd(d) {
    const p = (n) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

export default {
    name: 'MarketplaceProductManagement',
    data() {
        return {
            vendorId: this.$store.state.user.suppliercode || '',
            keyword: '',
            products: [],
            from30: '',
            loaded: false,
            busy: false,
            busyText: '',
            errorText: '',
            noticeText: '',
            collecting: false,
            fields: [
                { key: 'image', label: '' },
                { key: 'name', label: '상품명 / 옵션명' },
                { key: 'salePrice', label: '판매가', class: 'text-right', tdClass: 'text-nowrap' },
                { key: 'productStatus', label: '상태' },
                { key: 'stockQuantity', label: '쿠팡 표시 재고', class: 'text-right', tdClass: 'text-nowrap' },
                { key: 'myStock', label: '내가 적은 실제 재고' },
                { key: 'units7', label: '7일 판매량', class: 'text-right', tdClass: 'text-nowrap' },
                { key: 'units30', label: '30일 판매량', class: 'text-right', tdClass: 'text-nowrap' },
                { key: 'sales30', label: '30일 매출', class: 'text-right', tdClass: 'text-nowrap' },
            ],
        }
    },
    computed: {
        shown() {
            const k = this.keyword.trim()
            if (!k) return this.products
            return this.products.filter((p) => [p.productName, p.itemName, p.vendorItemId, p.barcode, p.sellerProductId].some((v) => String(v || '').includes(k)))
        },
    },
    mounted() {
        window.addEventListener('message', this.onExtensionMessage)
        if (!this.vendorId) {
            this.errorText = '이 쿠플러스 계정에 판매자코드가 없습니다.'
            return
        }
        this.search()
    },
    beforeDestroy() {
        window.removeEventListener('message', this.onExtensionMessage)
    },
    methods: {
        won(v) { return v === null || v === undefined ? '' : Math.round(v).toLocaleString() + '원' },
        count(v) { return v === null || v === undefined ? '' : Number(v).toLocaleString() },
        empty(v) { return v === null || v === undefined || v === '' },
        salesCell(v) { return this.empty(v) ? '안 모음' : this.count(v) },
        async search() {
            if (!this.vendorId) return
            this.busy = true
            this.busyText = '상품 조회 중...'
            this.errorText = ''
            try {
                const r = await axios.post('/api/marketplace/listProducts', { vendorId: this.vendorId, today: ymd(new Date()) })
                if (!r.data || !r.data.success) throw new Error((r.data && r.data.message) || '조회 실패')
                this.products = r.data.products
                this.from30 = r.data.from30
                this.loaded = true
            } catch (e) {
                console.error('[판매자배송 상품관리] 조회 실패:', e)
                this.errorText = '상품을 불러오지 못했습니다: ' + ((e.response && e.response.data && e.response.data.message) || e.message)
            } finally {
                this.busy = false
            }
        },
        collect() {
            this.collecting = true
            this.busy = true
            this.busyText = '확장에 상품 모으기를 요청했습니다...'
            this.errorText = ''
            window.postMessage({
                greeting: 'manualCollect',
                itemKey: 'marketplaceProducts',
                companyid: this.$store.state.user.companyid,
                suppliercode: this.vendorId,
                options: {},
            }, '*')
            // 확장이 없거나 멈추면 완료 알림이 안 온다 — 10분 뒤 크게 알린다(돌기만 하는 화면 0)
            setTimeout(() => {
                if (!this.collecting) return
                this.collecting = false
                this.busy = false
                this.errorText = '확장에서 10분 동안 완료 알림이 없습니다. 쿠플러스 확장과 쿠팡 윙 로그인을 확인해 주세요.'
            }, 10 * 60 * 1000)
        },
        onExtensionMessage(event) {
            if (event.source !== window || !event.data) return
            if (this.collecting && event.data.type === 'autoCollectStatus' && event.data.message) this.busyText = event.data.message
            if (event.data.type === 'manualCollectComplete' && event.data.itemKey === 'marketplaceProducts') {
                this.collecting = false
                this.busy = false
                if (event.data.success === false) this.errorText = '상품 모으기가 다 끝나지 않았습니다: ' + (event.data.error || '까닭 모름')
                else this.noticeText = '상품 모으기를 마쳤습니다.'
                this.search()
            }
        },
        async saveStock(row, value) {
            try {
                const r = await axios.post('/api/marketplace/saveStockMemo', { vendorId: this.vendorId, vendorItemId: row.vendorItemId, myStock: value === '' ? '' : Number(value) })
                if (!r.data || !r.data.success) throw new Error((r.data && r.data.message) || '저장 실패')
                row.myStock = r.data.removed ? null : r.data.myStock
            } catch (e) {
                console.error('[판매자배송 상품관리] 재고 저장 실패:', e)
                this.errorText = '실제 재고를 저장하지 못했습니다: ' + ((e.response && e.response.data && e.response.data.message) || e.message)
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.marketplace-products {
    &__search { width: 200px; }
    &__img { width: 48px; height: 48px; object-fit: cover; }
    &__name { min-width: 160px; max-width: 280px; white-space: normal; }
    &__stock { width: 90px; }
    // 한글 낱말 가운데서 안 꺾이게(390 stacked 머리 칸 포함) · stacked 에선 옵션 사이 굵은 줄
    ::v-deep th, ::v-deep td, ::v-deep td[data-label]::before { word-break: keep-all; }
    @media (max-width: 767.98px) { ::v-deep .b-table-stacked-md > tbody > tr + tr { border-top: 3px solid #dee2e6; } }
    // 390 — 검색칸이 줄어 «상품 모으기» 가 둘째 줄로 안 떨어지게
    @media (max-width: 575.98px) {
        &__tools { width: 100%; }
        &__search { width: auto; flex: 1 1 0; min-width: 0; }
    }
}
</style>
